import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import { OrganizationSchema, WebsiteSchema, WebPageSchema } from '@/components/feature/StructuredData';
import { comics, heroBanners, categories } from '@/mocks/comics';
import type { Comic } from '@/mocks/comics';

function ComicCard({ comic }: { comic: Comic }) {
  return (
    <Link
      to={`/comic/${comic.id}`}
      className="group block cursor-pointer"
    >
      <div className="relative overflow-hidden rounded-lg aspect-[3/4] bg-background-200">
        <img
          src={comic.coverImage}
          alt={`${comic.title} - 韩国漫画网免费在线阅读`}
          title={`${comic.title} - 韩漫免费阅读`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-3">
          <p className="text-white text-xs line-clamp-2">{comic.description}</p>
        </div>
        {comic.isNew && (
          <span className="absolute top-2 left-2 bg-accent-500 text-background-50 text-xs font-medium px-2 py-0.5 rounded-full">
            NEW
          </span>
        )}
        {comic.isHot && (
          <span className="absolute top-2 right-2 bg-primary-500 text-background-50 text-xs font-medium px-2 py-0.5 rounded-full">
            HOT
          </span>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent h-16"></div>
        <span className="absolute bottom-2 right-2 text-white/80 text-xs">{comic.status}</span>
      </div>
      <div className="mt-2 px-0.5">
        <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
          {comic.title}
        </h3>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-foreground-500">{comic.author}</span>
          <span className="text-xs text-foreground-400">|</span>
          <span className="text-xs text-accent-500 font-medium">{comic.rating}分</span>
        </div>
      </div>
    </Link>
  );
}

function StatsBar() {
  const stats = [
    { label: '漫画作品', value: '20,000+' },
    { label: '每日更新', value: '200+' },
    { label: '累计读者', value: '5,800,000+' },
    { label: '阅读次数', value: '3.2亿+' },
  ];

  return (
    <section className="w-full bg-primary-500">
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6 py-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, idx) => (
            <div key={idx} className="text-center">
              <p className="text-3xl md:text-4xl font-heading font-bold text-background-50">{stat.value}</p>
              <p className="text-sm text-primary-100 mt-1">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const [currentBanner, setCurrentBanner] = useState(0);

  const nextBanner = useCallback(() => {
    setCurrentBanner((prev) => (prev + 1) % heroBanners.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextBanner, 5000);
    return () => clearInterval(timer);
  }, [nextBanner]);

  const hotComics = comics.filter((c) => c.isHot).slice(0, 8);
  const newComics = comics.filter((c) => c.isNew).slice(0, 8);
  const recommendedComics = comics.filter((c) => c.isRecommended).slice(0, 8);
  const completedComics = comics.filter((c) => c.status === '已完结').slice(0, 8);
  const rankingComics = [...comics].sort((a, b) => b.views - a.views).slice(0, 10);
  const latestUpdates = [...comics].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()).slice(0, 12);

  return (
    <>
      <ScrollToTop />
      <SEOHead
        title="最新韩漫-免费在线阅读全集-韩国漫画网"
        description="韩国漫画网独家首发最新韩漫。所有作品均可免费在线阅读全集，无广告干扰，为您打造纯净的韩漫阅读环境。覆盖恋爱、奇幻、动作、悬疑等全类型韩漫资源。"
        keywords="最新韩漫,韩漫免费阅读,韩漫在线阅读全集,韩国漫画网,韩国漫画,免费漫画"
        canonicalUrl="https://www.koreacomic.cn/"
      />
      <OrganizationSchema />
      <WebsiteSchema />
      <WebPageSchema
        title="最新韩漫-免费在线阅读全集-韩国漫画网"
        description="韩国漫画网独家首发最新韩漫。所有作品均可免费在线阅读全集，无广告干扰。"
        url="https://www.koreacomic.cn/"
        datePublished="2024-01-01"
        dateModified="2026-08-04"
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Banner */}
          <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
            {heroBanners.map((banner, idx) => (
              <Link
                key={banner.id}
                to={`/comic/${banner.comicId}`}
                className={`absolute inset-0 transition-opacity duration-700 cursor-pointer ${
                  idx === currentBanner ? 'opacity-100 z-10' : 'opacity-0 z-0'
                }`}
              >
                <img
                  src={banner.image}
                  alt={banner.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/50"></div>
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-12 z-20">
                  <div className="max-w-screen-2xl mx-auto">
                    <span className="inline-block bg-accent-500 text-background-50 text-xs font-medium px-3 py-1 rounded-full mb-3">
                      重磅推荐
                    </span>
                    <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-2">
                      {banner.title}
                    </h2>
                    <p className="text-white/80 text-base md:text-lg max-w-xl">
                      {banner.subtitle}
                    </p>
                    <span className="inline-block mt-4 bg-primary-500 hover:bg-primary-600 text-background-50 px-6 py-2.5 rounded-full text-sm font-medium transition-colors cursor-pointer">
                      立即阅读
                    </span>
                  </div>
                </div>
              </Link>
            ))}

            <button
              onClick={() => setCurrentBanner((prev) => (prev - 1 + heroBanners.length) % heroBanners.length)}
              className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 text-white backdrop-blur-sm transition-all cursor-pointer"
              aria-label="上一张"
            >
              <i className="ri-arrow-left-s-line text-lg"></i>
            </button>
            <button
              onClick={nextBanner}
              className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/40 text-white backdrop-blur-sm transition-all cursor-pointer"
              aria-label="下一张"
            >
              <i className="ri-arrow-right-s-line text-lg"></i>
            </button>

            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2">
              {heroBanners.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentBanner(idx)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                    idx === currentBanner ? 'bg-white w-6' : 'bg-white/50'
                  }`}
                  aria-label={`第${idx + 1}张`}
                />
              ))}
            </div>
          </section>

          <StatsBar />

          {/* Category Quick Nav */}
          <section className="max-w-screen-2xl mx-auto px-4 md:px-6 py-10">
            <div className="flex items-center gap-2 flex-wrap">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={cat.slug === 'all' ? '/' : `/category/${cat.slug}`}
                  className="px-4 py-2 text-sm font-medium rounded-full bg-background-100 text-foreground-700 hover:bg-primary-500 hover:text-background-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </section>

          {/* Hot Comics */}
          <section className="max-w-screen-2xl mx-auto px-4 md:px-6 pb-14" data-product-shop>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  热门推荐
                </h2>
                <span className="w-8 h-0.5 bg-accent-500 rounded-full mt-1"></span>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line text-xs"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4 md:gap-5">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </section>

          {/* New Comics */}
          <section className="bg-background-100/70 py-14" data-product-shop>
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                    最新上架
                  </h2>
                  <span className="w-8 h-0.5 bg-accent-500 rounded-full mt-1"></span>
                </div>
                <Link
                  to="/category/all"
                  className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4 md:gap-5">
                {newComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </section>

          {/* Two Column: Ranking + Recommended */}
          <section className="max-w-screen-2xl mx-auto px-4 md:px-6 py-14">
            <div className="flex flex-col lg:flex-row gap-8">
              {/* Ranking */}
              <div className="lg:w-[380px] flex-shrink-0">
                <div className="flex items-center gap-3 mb-6">
                  <h2 className="text-xl font-heading font-bold text-foreground-900">
                    人气排行
                  </h2>
                  <span className="w-8 h-0.5 bg-primary-500 rounded-full mt-1"></span>
                </div>
                <div className="bg-background-100 rounded-lg p-4">
                  {rankingComics.map((comic, idx) => (
                    <Link
                      key={comic.id}
                      to={`/comic/${comic.id}`}
                      className="flex items-center gap-3 py-3 border-b border-background-200 last:border-0 hover:bg-background-50/50 rounded-md px-2 -mx-2 transition-colors cursor-pointer group"
                    >
                      <span
                        className={`w-7 h-7 flex items-center justify-center rounded-full text-xs font-bold flex-shrink-0 ${
                          idx < 3
                            ? 'bg-primary-500 text-background-50'
                            : 'bg-background-200 text-foreground-500'
                        }`}
                      >
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                          {comic.title}
                        </p>
                        <p className="text-xs text-foreground-500">{comic.author}</p>
                      </div>
                      <span className="text-xs text-accent-500 font-medium whitespace-nowrap">{comic.rating}分</span>
                    </Link>
                  ))}
                </div>
              </div>

              {/* Recommended Grid */}
              <div className="flex-1" data-product-shop>
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl font-heading font-bold text-foreground-900">
                      编辑推荐
                    </h2>
                    <span className="w-8 h-0.5 bg-secondary-500 rounded-full mt-1"></span>
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {recommendedComics.map((comic) => (
                    <ComicCard key={comic.id} comic={comic} />
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Completed Comics */}
          <section className="bg-background-100/70 py-14" data-product-shop>
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                    已完结佳作
                  </h2>
                  <span className="w-8 h-0.5 bg-secondary-500 rounded-full mt-1"></span>
                </div>
                <Link
                  to="/category/all"
                  className="text-sm text-foreground-500 hover:text-primary-500 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
                >
                  查看全部 <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-4 gap-4 md:gap-5">
                {completedComics.map((comic) => (
                  <ComicCard key={comic.id} comic={comic} />
                ))}
              </div>
            </div>
          </section>

          {/* Latest Updates */}
          <section className="max-w-screen-2xl mx-auto px-4 md:px-6 py-14">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                  最近更新
                </h2>
                <span className="w-8 h-0.5 bg-accent-500 rounded-full mt-1"></span>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {latestUpdates.map((comic) => (
                <Link
                  key={comic.id}
                  to={`/comic/${comic.id}`}
                  className="flex items-center gap-3 p-3 bg-background-100 rounded-lg hover:bg-background-200/70 transition-colors cursor-pointer group"
                >
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    className="w-12 h-16 object-cover object-top rounded flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                      {comic.title}
                    </p>
                    <p className="text-xs text-foreground-500 mt-0.5">
                      {comic.author} · 更新至第{comic.totalChapters}话
                    </p>
                  </div>
                  <span className="text-xs text-foreground-400 whitespace-nowrap">{comic.updateDate}</span>
                </Link>
              ))}
            </div>
          </section>

          {/* CTAs */}
          <section className="bg-primary-500 py-14">
            <div className="max-w-screen-2xl mx-auto px-4 md:px-6 text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">
                开始您的韩漫阅读之旅
              </h2>
              <p className="text-primary-100 text-base mb-6 max-w-lg mx-auto">
                两万多部韩漫作品，全部免费在线阅读。随时随地，畅享纯净阅读体验。
              </p>
              <div className="flex items-center justify-center gap-3 flex-wrap">
                <Link
                  to="/category/all"
                  className="inline-flex items-center gap-2 bg-background-50 text-primary-600 hover:bg-primary-50 px-6 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-book-open-line"></i>
                  浏览全部漫画
                </Link>
                <Link
                  to="/about"
                  className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-background-50 px-6 py-3 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-information-line"></i>
                  了解更多
                </Link>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}